import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getWarehouses = createAsyncThunk(
  "Warehouse/get",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(`${API_URL.WAREHOUSE.SLUG}?${queryString}`);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createWarehouse = createAsyncThunk(
  "Warehouse/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL.WAREHOUSE.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Lookup variant — hits the permission-free /lookup endpoint. Used by dropdowns
// on screens of other modules; the management list keeps using getWarehouses.
export const getWarehousesLookup = createAsyncThunk(
  "Warehouse/getLookup",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(`${API_URL.WAREHOUSE.LOOKUP}?${queryString}`);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Returns the tenant-wide default Warehouse or null.
// Dropdown-only (not used by the Warehouse management list) — points at the
// permission-free /lookup endpoint so other modules' forms can read it.
export const getDefaultWarehouse = createAsyncThunk(
  "Warehouse/getDefault",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.WAREHOUSE.DEFAULT_LOOKUP);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteWarehouse = createAsyncThunk(
  "Warehouse/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.WAREHOUSE.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const editWarehouse = createAsyncThunk(
  "Warehouse/put",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.WAREHOUSE.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const enableBinManagement = createAsyncThunk(
  "Warehouse/enableBinManagement",
  async (warehouseId, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.WAREHOUSE.ENABLE_BIN_MANAGEMENT(warehouseId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const disableBinManagement = createAsyncThunk(
  "Warehouse/disableBinManagement",
  async (warehouseId, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.WAREHOUSE.DISABLE_BIN_MANAGEMENT(warehouseId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const toggleWarehouseStatus = createAsyncThunk(
  "Warehouse/toggleStatus",
  async (warehouseId, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.WAREHOUSE.TOGGLE_STATUS(warehouseId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const warehouseSlice = createSlice({
  name: "warehouse",
  initialState: {
    warehouse_list: [],
    current_values: null,
    active_payload: null,
  },
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWarehouses.fulfilled, (state, action) => {
      state.warehouse_list = action.payload.data.list;
    });
    builder.addCase(getWarehousesLookup.fulfilled, (state, action) => {
      state.warehouse_list = action.payload.data.list;
    });
  },
});

export const { setCurrentValues, setActivePayload, resetCurrentValue } =
  warehouseSlice.actions;
export default warehouseSlice.reducer;